/**
 * useOfflineDetection Hook
 * 
 * Detects when the user goes offline/online and provides online status.
 * Useful for showing offline indicators and disabling actions when offline.
 */

import { useState, useEffect } from 'react';

/**
 * Custom hook to detect online/offline status
 * @returns {Object} { isOnline, wasOffline }
 */
export const useOfflineDetection = () => {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    // Set initial state
    setIsOnline(navigator.onLine);

    const handleOnline = () => {
      setIsOnline(true);
      setWasOffline(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    // Listen to online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline, wasOffline };
};

export default useOfflineDetection;

